import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ActionReportListView } from "../../common/ActionReportListView/ActionReportListView";
import { CollapsibleSection } from "../../common/CollapsibleSection/CollapsibleSection";
import defaultContent from "../../../../content/actionReportsBirthdays";
import "./BirthdayGroup.scss";
import { birthdayProfileTrack } from "../../../../common/tealium/ActionReports/BirthdayARTrack";

export interface BirthdayEntry {
  aff: string;
  abo: string;
  aboName?: string;
  birthDate?: string;
  sortOrder?: number;
  nameTypeFlag?: string;
  confidentialUser?: boolean;
  phoneNumber?: string;
  whatsapp?: string;
  level?: number;
  imageRefUrl?: string;
}

type Props = {
  title: string;
  entries: BirthdayEntry[];
  scrollToAbo?: { aff: string; abo: string };
  expandKey?: unknown;
  "data-testid"?: string;
};

export const BirthdayGroup = ({ title, entries, scrollToAbo, expandKey, "data-testid": dataTestId }: Props) => {
  const { t } = useTranslation(["actionReportsBirthdays"]);

  // Find the index of the searched ABO within the entries
  let scrollToIndex: number | undefined = undefined;
  if (scrollToAbo) {
    scrollToIndex = entries.findIndex((entry) => entry.aff === scrollToAbo.aff && entry.abo === scrollToAbo.abo);
    if (scrollToIndex === -1) {
      scrollToIndex = undefined;
    }
  }

  const handleAboNameClick = useCallback(() => {
    birthdayProfileTrack();
  }, []);

  if (entries.length === 0) {
    return <></>;
  }

  const tableColumns = t("table.columns", {
    ns: "actionReportsBirthdays",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as { [key: string]: string };

  const birthDateTitle = tableColumns["birthDate"] || defaultContent.table.columns.birthDate;
  const bonusPercentTitle = tableColumns["bonusPercent"] || defaultContent.table.columns.bonusPercent;
  const groupPVTitle = tableColumns["groupPV"] || defaultContent.table.columns.groupPV;

  const reportData = entries.map((entry) => ({
    aff: entry.aff,
    abo: entry.abo,
    aboName: entry.confidentialUser ? t("confidentialUser", "Confidential User") : entry.aboName || "",
    aboNumber: entry.abo,
    birthDate: entry.birthDate,
    phoneNumber: entry.phoneNumber,
    whatsapp: entry.whatsapp,
    level: entry.level,
    imageRefUrl: entry.imageRefUrl,
  }));

  // birthDate renders from the payload; bonusPercent/groupPV are fetched per row
  // via the shared ABO enrichment API (same source the export uses) and rendered
  // by getKpiValue, so the component invents no values.
  const selectedKpis = ["birthDate", "bonusPercent", "groupPV"];
  const fieldConfig = [
    { id: "birthDate", kpi: "birthDate", title: birthDateTitle },
    { id: "bonusPercent", kpi: "bonusPercent", title: bonusPercentTitle },
    { id: "groupPV", kpi: "groupPV", title: groupPVTitle },
  ];

  return (
    <CollapsibleSection
      label={title}
      count={entries.length}
      birthdayGroupScrollToAbo={scrollToAbo}
      expandKey={expandKey}
      data-testid={dataTestId}
    >
      <ActionReportListView
        selectedKpis={selectedKpis}
        reportId="birthdaysAR"
        reportData={reportData}
        fieldConfig={fieldConfig}
        enableWatchlist={true}
        aboHeader={"applicantName"}
        trackABONameClick={handleAboNameClick}
        scrollToIndex={scrollToIndex}
        showImageBlock={true}
      />
    </CollapsibleSection>
  );
};
